import React from 'react';
import { projectService, selectIsDesignSystemExtracting, useAppSelector } from '@ideoz/core';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Button,
  Label,
  Icon,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@ideoz/ui';
import ColorPicker from './ColorPicker';
import { Check, Loader2, Sparkles, X } from 'lucide-react';
import { DesignSystemPageLayout } from './DesignSystemPageLayout';
import type { DesignSystemSectionId } from '../designSystemConstants';
import { ARABIC_FONTS, POPULAR_FONTS, elevationToShadows } from '../designSystemConstants';

export type UxPatterns = {
  addPattern?: 'modal' | 'drawer-right' | 'drawer-bottom' | 'new-page' | 'inline-row';
  editPattern?: 'modal' | 'drawer-right' | 'drawer-bottom' | 'new-page' | 'inline-row';
  detailPattern?: 'page' | 'side-panel' | 'modal' | 'expand-row';
  deletePattern?: 'confirm-modal' | 'undo-toast' | 'swipe';
  navPattern?: 'top-bar' | 'side-nav' | 'bottom-tabs' | 'drawer' | 'rail';
  listPattern?: 'table' | 'cards' | 'list-rows' | 'kanban' | 'feed';
  containerWidth?: 'narrow-centered' | 'wide' | 'full-bleed';
  sidebarLayout?: 'persistent-left' | 'persistent-right' | 'collapsible' | 'none';
  searchPattern?: 'top-bar-inline' | 'global-modal' | 'page-header' | 'none';
  filterPattern?: 'top-bar' | 'side-panel' | 'modal' | 'inline-dropdowns';
  loadingPattern?: 'spinner' | 'skeleton' | 'shimmer' | 'progress-bar';
  emptyStatePattern?: 'illustration-cta' | 'minimal-text' | 'helper-guidance';
};

export type DesignSystem = {
  id?: string;
  projectId?: string;
  primaryColor?: string;
  primaryForeground?: string;
  secondaryColor?: string;
  secondaryForeground?: string;
  fontFamily?: string;
  headingFontFamily?: string;
  theme?: 'light' | 'dark';
  appBg?: string;
  surfaceColor?: string;
  borderColor?: string;
  mutedTextColor?: string;
  successColor?: string;
  warningColor?: string;
  errorColor?: string;
  infoColor?: string;
  borderRadius?: 'none' | 'sm' | 'md' | 'lg' | 'full';
  shadows?: 'none' | 'small' | 'medium' | 'large';
  density?: 'compact' | 'comfortable' | 'spacious';
  elevationStyle?: 'flat' | 'soft-shadow' | 'material' | 'glass' | 'neumorphism';
  buttonStyle?: 'filled' | 'outlined' | 'ghost' | 'gradient' | 'pill';
  iconStyle?: 'outline' | 'filled' | 'duotone';
  inputStyle?: 'outlined' | 'filled' | 'underlined' | 'floating-label';
  uxPatterns?: UxPatterns | null;
};

type SelectOption<T extends string> = { value: T; label: string };

export const BORDER_RADIUS_OPTIONS: Array<DesignSystem['borderRadius']> = [
  'none',
  'sm',
  'md',
  'lg',
  'full',
];

export const SHADOWS_OPTIONS: Array<DesignSystem['shadows']> = ['none', 'small', 'medium', 'large'];

export const DENSITY_OPTIONS: Array<SelectOption<NonNullable<DesignSystem['density']>>> = [
  { value: 'compact', label: 'Compact' },
  { value: 'comfortable', label: 'Comfortable' },
  { value: 'spacious', label: 'Spacious' },
];

export const ELEVATION_STYLE_OPTIONS: Array<
  SelectOption<NonNullable<DesignSystem['elevationStyle']>>
> = [
  { value: 'flat', label: 'Flat' },
  { value: 'soft-shadow', label: 'Soft shadow' },
  { value: 'material', label: 'Material' },
  { value: 'glass', label: 'Glass' },
  { value: 'neumorphism', label: 'Neumorphism' },
];

export const BUTTON_STYLE_OPTIONS: Array<
  SelectOption<NonNullable<DesignSystem['buttonStyle']>>
> = [
  { value: 'filled', label: 'Filled' },
  { value: 'outlined', label: 'Outlined' },
  { value: 'ghost', label: 'Ghost' },
  { value: 'gradient', label: 'Gradient' },
  { value: 'pill', label: 'Pill' },
];

export const ICON_STYLE_OPTIONS: Array<SelectOption<NonNullable<DesignSystem['iconStyle']>>> = [
  { value: 'outline', label: 'Outline' },
  { value: 'filled', label: 'Filled' },
  { value: 'duotone', label: 'Duotone' },
];

export const INPUT_STYLE_OPTIONS: Array<SelectOption<NonNullable<DesignSystem['inputStyle']>>> = [
  { value: 'outlined', label: 'Outlined' },
  { value: 'filled', label: 'Filled' },
  { value: 'underlined', label: 'Underlined' },
  { value: 'floating-label', label: 'Floating label' },
];

export const FORM_PATTERN_OPTIONS: Array<
  SelectOption<NonNullable<UxPatterns['addPattern']>>
> = [
  { value: 'modal', label: 'Modal dialog' },
  { value: 'drawer-right', label: 'Side drawer (right)' },
  { value: 'drawer-bottom', label: 'Bottom drawer' },
  { value: 'new-page', label: 'New page' },
  { value: 'inline-row', label: 'Inline row' },
];

export const DETAIL_PATTERN_OPTIONS: Array<
  SelectOption<NonNullable<UxPatterns['detailPattern']>>
> = [
  { value: 'page', label: 'Dedicated page' },
  { value: 'side-panel', label: 'Side panel' },
  { value: 'modal', label: 'Modal' },
  { value: 'expand-row', label: 'Expand row' },
];

export const DELETE_PATTERN_OPTIONS: Array<
  SelectOption<NonNullable<UxPatterns['deletePattern']>>
> = [
  { value: 'confirm-modal', label: 'Confirm modal' },
  { value: 'undo-toast', label: 'Undo toast' },
  { value: 'swipe', label: 'Swipe to delete' },
];

export const NAV_PATTERN_OPTIONS: Array<
  SelectOption<NonNullable<UxPatterns['navPattern']>>
> = [
  { value: 'top-bar', label: 'Top bar' },
  { value: 'side-nav', label: 'Side navigation' },
  { value: 'bottom-tabs', label: 'Bottom tabs' },
  { value: 'drawer', label: 'Drawer / hamburger' },
  { value: 'rail', label: 'Navigation rail' },
];

export const LIST_PATTERN_OPTIONS: Array<
  SelectOption<NonNullable<UxPatterns['listPattern']>>
> = [
  { value: 'table', label: 'Table (rows + headers)' },
  { value: 'cards', label: 'Card grid' },
  { value: 'list-rows', label: 'List rows' },
  { value: 'kanban', label: 'Kanban board' },
  { value: 'feed', label: 'Feed' },
];

export const CONTAINER_WIDTH_OPTIONS: Array<
  SelectOption<NonNullable<UxPatterns['containerWidth']>>
> = [
  { value: 'narrow-centered', label: 'Narrow centered' },
  { value: 'wide', label: 'Wide' },
  { value: 'full-bleed', label: 'Full bleed' },
];

export const SIDEBAR_LAYOUT_OPTIONS: Array<
  SelectOption<NonNullable<UxPatterns['sidebarLayout']>>
> = [
  { value: 'persistent-left', label: 'Persistent (left)' },
  { value: 'persistent-right', label: 'Persistent (right)' },
  { value: 'collapsible', label: 'Collapsible' },
  { value: 'none', label: 'No sidebar' },
];

export const SEARCH_PATTERN_OPTIONS: Array<
  SelectOption<NonNullable<UxPatterns['searchPattern']>>
> = [
  { value: 'top-bar-inline', label: 'Top-bar inline' },
  { value: 'global-modal', label: 'Global modal / palette' },
  { value: 'page-header', label: 'Page header' },
  { value: 'none', label: 'No search' },
];

export const FILTER_PATTERN_OPTIONS: Array<
  SelectOption<NonNullable<UxPatterns['filterPattern']>>
> = [
  { value: 'top-bar', label: 'Top bar' },
  { value: 'side-panel', label: 'Side panel' },
  { value: 'modal', label: 'Modal' },
  { value: 'inline-dropdowns', label: 'Inline dropdowns' },
];

export const LOADING_PATTERN_OPTIONS: Array<
  SelectOption<NonNullable<UxPatterns['loadingPattern']>>
> = [
  { value: 'spinner', label: 'Spinner' },
  { value: 'skeleton', label: 'Skeleton' },
  { value: 'shimmer', label: 'Shimmer' },
  { value: 'progress-bar', label: 'Progress bar' },
];

export const EMPTY_STATE_PATTERN_OPTIONS: Array<
  SelectOption<NonNullable<UxPatterns['emptyStatePattern']>>
> = [
  { value: 'illustration-cta', label: 'Illustration + CTA' },
  { value: 'minimal-text', label: 'Minimal text' },
  { value: 'helper-guidance', label: 'Helper guidance' },
];

export type Logo = {
  id?: string;
  projectId?: string;
  fileName?: string;
  file?: File;
  mimeType?: string;
  fileSize?: number;
};

export type DesignSystemPanelProps = {
  projectId?: string | null;
  /** Called after a successful save / reset with the refreshed payload. */
  onUpdated?: (payload: { designSystem?: DesignSystem; logo?: Logo } | null) => void;
  /** Called after a successful save / reset (e.g. to close a drawer). */
  onAfterSave?: () => void;
  /** Cancel handler — when omitted, no Cancel button is rendered. */
  onCancel?: () => void;
  /** Loaded into the form when the panel mounts; refetched whenever projectId changes. */
  autoLoad?: boolean;
  /** Full three-column page layout, or compact single-column form (drawer). */
  layout?: 'page' | 'compact';
};

const DEFAULT_FONTS = [...POPULAR_FONTS, ...ARABIC_FONTS.filter((f) => !POPULAR_FONTS.includes(f))];

export const DEFAULT_UX_PATTERNS: UxPatterns = {
  addPattern: 'modal',
  editPattern: 'modal',
  detailPattern: 'page',
  deletePattern: 'confirm-modal',
  navPattern: 'top-bar',
  listPattern: 'table',
  containerWidth: 'wide',
  sidebarLayout: 'none',
  searchPattern: 'top-bar-inline',
  filterPattern: 'top-bar',
  loadingPattern: 'spinner',
  emptyStatePattern: 'minimal-text',
};

export const DEFAULT_DESIGN_SYSTEM: DesignSystem = {
  primaryColor: '#334155',
  primaryForeground: '#FFFFFF',
  secondaryColor: '#64748B',
  secondaryForeground: '#FFFFFF',
  fontFamily: 'Exo 2',
  headingFontFamily: undefined,
  theme: 'light',
  appBg: '#FFFFFF',
  surfaceColor: '#F8FAFC',
  borderColor: '#E2E8F0',
  mutedTextColor: '#64748B',
  successColor: '#22C55E',
  warningColor: '#EAB308',
  errorColor: '#EF4444',
  infoColor: '#3B82F6',
  borderRadius: 'md',
  shadows: 'none',
  density: 'comfortable',
  elevationStyle: 'flat',
  buttonStyle: 'filled',
  iconStyle: 'outline',
  inputStyle: 'outlined',
  uxPatterns: DEFAULT_UX_PATTERNS,
};

export const DesignSystemPanel: React.FC<DesignSystemPanelProps> = ({
  projectId,
  onUpdated,
  onAfterSave,
  onCancel,
  autoLoad = true,
  layout = 'page',
}) => {
  const [saving, setSaving] = React.useState(false);
  const [designSystem, setDesignSystem] = React.useState<DesignSystem>({
    ...DEFAULT_DESIGN_SYSTEM,
  });
  const [logo, setLogo] = React.useState<Logo | null>(null);
  const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);
  const [logoRemoved, setLogoRemoved] = React.useState(false);
  const [initialHasLogo, setInitialHasLogo] = React.useState(false);
  const [removingLogo] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const [isDragOver, setIsDragOver] = React.useState(false);
  const [hasUpdatedDesignSystem, setHasUpdatedDesignSystem] = React.useState(false);
  const [showResetDialog, setShowResetDialog] = React.useState(false);
  // Hides the form until the initial GET /design-system fetch resolves — otherwise
  // the DEFAULT_DESIGN_SYSTEM values flash for a moment before the saved values
  // replace them, which reads as "we overwrote your work."
  const [isLoadingDesignSystem, setIsLoadingDesignSystem] = React.useState<boolean>(
    autoLoad && !!projectId,
  );
  // Onboarding fires design-system extraction as fire-and-forget; if the user
  // navigates here before it finishes, this flag makes the panel wait rather
  // than show stale/default values. When Onboarding clears the flag on
  // completion, the effect below re-fetches.
  const isExtractingFromOnboarding = useAppSelector((s) =>
    selectIsDesignSystemExtracting(s, projectId ?? null),
  );
  const [extractFiles, setExtractFiles] = React.useState<File[]>([]);
  const [extractPreviews, setExtractPreviews] = React.useState<string[]>([]);
  const [isExtracting, setIsExtracting] = React.useState(false);
  const extractInputRef = React.useRef<HTMLInputElement | null>(null);
  const [isDirty, setIsDirty] = React.useState(false);
  const sectionRefs = {
    brand: React.useRef<HTMLElement>(null),
    colors: React.useRef<HTMLElement>(null),
    typography: React.useRef<HTMLElement>(null),
    'visual-style': React.useRef<HTMLElement>(null),
    patterns: React.useRef<HTMLElement>(null),
  };
  const centerScrollRef = React.useRef<HTMLDivElement>(null);

  const markDirty = React.useCallback(() => setIsDirty(true), []);

  const updateDesignSystem: React.Dispatch<React.SetStateAction<DesignSystem>> = React.useCallback(
    (updater) => {
      markDirty();
      setDesignSystem(updater);
    },
    [markDirty],
  );

  React.useEffect(() => {
    const urls = extractFiles.map((f) => URL.createObjectURL(f));
    setExtractPreviews(urls);
    return () => {
      urls.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [extractFiles]);

  const addExtractFiles = (incoming: FileList | File[] | null | undefined) => {
    if (!incoming) return;
    const list = Array.from(incoming);
    const allowed = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    const filtered: File[] = [];
    for (const f of list) {
      if (!allowed.includes(f.type)) {
        toast.error(`Skipped ${f.name}: only PNG / JPEG / WEBP.`);
        continue;
      }
      if (f.size > 4 * 1024 * 1024) {
        toast.error(`Skipped ${f.name}: over 4 MB.`);
        continue;
      }
      filtered.push(f);
    }
    setExtractFiles((prev) => {
      const combined = [...prev, ...filtered].slice(0, 6);
      if (prev.length + filtered.length > 6) {
        toast.error('Only the first 6 screenshots will be used.');
      }
      return combined;
    });
  };

  const removeExtractFile = (idx: number) => {
    setExtractFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleExtract = async () => {
    if (extractFiles.length === 0) return;
    setIsExtracting(true);
    try {
      const extracted = (await projectService.extractDesignSystem(
        extractFiles,
      )) as Partial<DesignSystem> & { reasoning?: string | null };

      if (extracted?.reasoning) {
        // Surface the model's per-extraction reasoning to the console so the
        // UX team can eyeball why specific fields were / weren't detected.
        console.info('[design-system-extract] reasoning:', extracted.reasoning);
      }

      const topKeys: (keyof DesignSystem)[] = [
        'primaryColor',
        'primaryForeground',
        'secondaryColor',
        'secondaryForeground',
        'fontFamily',
        'headingFontFamily',
        'theme',
        'appBg',
        'surfaceColor',
        'borderColor',
        'mutedTextColor',
        'successColor',
        'warningColor',
        'errorColor',
        'infoColor',
        'borderRadius',
        'shadows',
        'density',
        'elevationStyle',
        'buttonStyle',
        'iconStyle',
        'inputStyle',
      ];
      const uxKeys: (keyof UxPatterns)[] = [
        'addPattern',
        'editPattern',
        'detailPattern',
        'deletePattern',
        'navPattern',
        'listPattern',
        'containerWidth',
        'sidebarLayout',
        'searchPattern',
        'filterPattern',
        'loadingPattern',
        'emptyStatePattern',
      ];

      let filled = 0;
      markDirty();
      setDesignSystem((prev) => {
        const next: DesignSystem = { ...prev };
        for (const k of topKeys) {
          const v = extracted[k];
          if (v != null) {
            (next as any)[k] = v;
            filled += 1;
          }
        }
        const extractedUx = extracted.uxPatterns ?? undefined;
        if (extractedUx) {
          const nextUx: UxPatterns = { ...(prev.uxPatterns ?? {}) };
          for (const k of uxKeys) {
            const v = extractedUx[k];
            if (v != null) {
              (nextUx as any)[k] = v;
              filled += 1;
            }
          }
          next.uxPatterns = nextUx;
        }
        return next;
      });

      const total = 34; // 22 top-level + 12 ux
      setHasUpdatedDesignSystem(true);
      setExtractFiles([]);
      if (extractInputRef.current) extractInputRef.current.value = '';
      toast.success(
        `Extracted ${filled} of ${total} fields — review before saving.`,
      );
    } catch (err: any) {
      toast.error(err?.message || 'Failed to extract design system');
    } finally {
      setIsExtracting(false);
    }
  };

  React.useEffect(() => {
    if (!autoLoad || !projectId) {
      setIsLoadingDesignSystem(false);
      return;
    }
    // Skip the fetch entirely while onboarding's extract is still in flight —
    // the panel will show its loader (see `isExtractingFromOnboarding` below)
    // and this effect will re-run once the flag flips off.
    if (isExtractingFromOnboarding) {
      setIsLoadingDesignSystem(true);
      return;
    }
    setIsLoadingDesignSystem(true);
    (async () => {
      try {
        const data = await projectService.getDesignSystem(projectId);
        const item = Array.isArray(data) && data.length > 0 ? data[0] : data;

        if (item?.designSystem) {
          const fetched = item.designSystem as DesignSystem;
          setDesignSystem(fetched);

          const fetchedUx = fetched.uxPatterns ?? {};
          const isCustomized =
            fetched.primaryColor !== DEFAULT_DESIGN_SYSTEM.primaryColor ||
            fetched.primaryForeground !== DEFAULT_DESIGN_SYSTEM.primaryForeground ||
            fetched.secondaryColor !== DEFAULT_DESIGN_SYSTEM.secondaryColor ||
            fetched.secondaryForeground !== DEFAULT_DESIGN_SYSTEM.secondaryForeground ||
            fetched.fontFamily !== DEFAULT_DESIGN_SYSTEM.fontFamily ||
            fetched.theme !== DEFAULT_DESIGN_SYSTEM.theme ||
            fetched.appBg !== DEFAULT_DESIGN_SYSTEM.appBg ||
            fetched.borderRadius !== DEFAULT_DESIGN_SYSTEM.borderRadius ||
            fetched.shadows !== DEFAULT_DESIGN_SYSTEM.shadows ||
            fetched.density !== DEFAULT_DESIGN_SYSTEM.density ||
            fetched.elevationStyle !== DEFAULT_DESIGN_SYSTEM.elevationStyle ||
            fetched.buttonStyle !== DEFAULT_DESIGN_SYSTEM.buttonStyle ||
            fetched.iconStyle !== DEFAULT_DESIGN_SYSTEM.iconStyle ||
            fetched.inputStyle !== DEFAULT_DESIGN_SYSTEM.inputStyle ||
            fetched.headingFontFamily !== DEFAULT_DESIGN_SYSTEM.headingFontFamily ||
            fetched.surfaceColor !== DEFAULT_DESIGN_SYSTEM.surfaceColor ||
            fetched.borderColor !== DEFAULT_DESIGN_SYSTEM.borderColor ||
            fetched.mutedTextColor !== DEFAULT_DESIGN_SYSTEM.mutedTextColor ||
            fetched.successColor !== DEFAULT_DESIGN_SYSTEM.successColor ||
            fetched.warningColor !== DEFAULT_DESIGN_SYSTEM.warningColor ||
            fetched.errorColor !== DEFAULT_DESIGN_SYSTEM.errorColor ||
            fetched.infoColor !== DEFAULT_DESIGN_SYSTEM.infoColor ||
            fetchedUx.addPattern !== DEFAULT_UX_PATTERNS.addPattern ||
            fetchedUx.editPattern !== DEFAULT_UX_PATTERNS.editPattern ||
            fetchedUx.detailPattern !== DEFAULT_UX_PATTERNS.detailPattern ||
            fetchedUx.deletePattern !== DEFAULT_UX_PATTERNS.deletePattern ||
            fetchedUx.navPattern !== DEFAULT_UX_PATTERNS.navPattern ||
            fetchedUx.listPattern !== DEFAULT_UX_PATTERNS.listPattern ||
            fetchedUx.containerWidth !== DEFAULT_UX_PATTERNS.containerWidth ||
            fetchedUx.sidebarLayout !== DEFAULT_UX_PATTERNS.sidebarLayout ||
            fetchedUx.searchPattern !== DEFAULT_UX_PATTERNS.searchPattern ||
            fetchedUx.filterPattern !== DEFAULT_UX_PATTERNS.filterPattern ||
            fetchedUx.loadingPattern !== DEFAULT_UX_PATTERNS.loadingPattern ||
            fetchedUx.emptyStatePattern !== DEFAULT_UX_PATTERNS.emptyStatePattern;

          setHasUpdatedDesignSystem(isCustomized);
        } else {
          setDesignSystem({ ...DEFAULT_DESIGN_SYSTEM });
          setHasUpdatedDesignSystem(false);
        }

        if (item?.logo) {
          const serverLogo = item.logo as any;
          setInitialHasLogo(true);
          if (serverLogo.fileContent) {
            const mime = serverLogo.mimeType || 'image/png';
            setPreviewUrl(`data:${mime};base64,${serverLogo.fileContent}`);
            setLogo({
              id: serverLogo.id,
              projectId: serverLogo.projectId,
              fileName: serverLogo.fileName,
              mimeType: serverLogo.mimeType,
              fileSize: serverLogo.fileSize,
            });
          } else if (typeof serverLogo === 'string') {
            setPreviewUrl(serverLogo);
            setLogo({ fileName: undefined } as any);
          } else if (serverLogo.url || serverLogo.fileUrl) {
            setPreviewUrl(serverLogo.url || serverLogo.fileUrl);
            setLogo({ fileName: serverLogo.fileName } as any);
          } else {
            setLogo(serverLogo as Logo);
          }
          setLogoRemoved(false);
          setHasUpdatedDesignSystem(true);
        } else {
          setInitialHasLogo(false);
        }
      } catch (err) {
        console.error(err);
        setDesignSystem({ ...DEFAULT_DESIGN_SYSTEM });
        setHasUpdatedDesignSystem(false);
      } finally {
        setIsLoadingDesignSystem(false);
        setIsDirty(false);
      }
    })();
  }, [autoLoad, projectId, isExtractingFromOnboarding]);

  React.useEffect(() => {
    if (logo?.file) {
      const url = URL.createObjectURL(logo.file);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    return;
  }, [logo]);

  const handleFileChange = (file?: File | null) => {
    setLogoRemoved(false);
    markDirty();
    if (!file) {
      setLogo(null);
      setPreviewUrl(null);
      return;
    }

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Only JPG, PNG, and SVG files are allowed.');
      return;
    }
    const maxSize = 4 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('File size exceeds 4 MB. Please upload a smaller file.');
      return;
    }

    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    setLogo({ fileName: file.name, file, mimeType: file.type, fileSize: file.size });
  };

  const handleHiddenInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) handleFileChange(f);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRemove = (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    markDirty();
    setLogo(null);
    setPreviewUrl(null);
    setLogoRemoved(true);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) handleFileChange(f);
  };

  const handleSave = async () => {
    if (!projectId) return;
    setSaving(true);
    try {
      const payload = {
        primaryColor: designSystem.primaryColor,
        primaryForeground: designSystem.primaryForeground,
        secondaryColor: designSystem.secondaryColor,
        secondaryForeground: designSystem.secondaryForeground,
        fontFamily: designSystem.fontFamily,
        headingFontFamily: designSystem.headingFontFamily,
        theme: designSystem.theme,
        appBg: designSystem.appBg,
        surfaceColor: designSystem.surfaceColor,
        borderColor: designSystem.borderColor,
        mutedTextColor: designSystem.mutedTextColor,
        successColor: designSystem.successColor,
        warningColor: designSystem.warningColor,
        errorColor: designSystem.errorColor,
        infoColor: designSystem.infoColor,
        borderRadius: designSystem.borderRadius,
        // `shadows` is derived from the elevation style (the control the page
        // exposes) so the persisted DTO never ships a stale `none` shadow.
        shadows: elevationToShadows(designSystem.elevationStyle),
        density: designSystem.density,
        elevationStyle: designSystem.elevationStyle,
        buttonStyle: designSystem.buttonStyle,
        iconStyle: designSystem.iconStyle,
        inputStyle: designSystem.inputStyle,
        uxPatterns: designSystem.uxPatterns ?? undefined,
      };

      try {
        await projectService.updateDesignSystem(projectId, payload);
      } catch (err: any) {
        toast.error(err?.message || 'Failed to update design system');
        return;
      }

      if (logoRemoved && initialHasLogo) {
        try {
          await projectService.deleteDesignSystemLogo(projectId);
          setInitialHasLogo(false);
        } catch (err: any) {
          toast.error(err?.message || 'Failed to remove logo');
          return;
        }
      }

      if (logo?.file) {
        try {
          const fd = new FormData();
          fd.append('logo', logo.file);
          await projectService.uploadDesignSystemLogo(projectId, fd);
        } catch (err: any) {
          toast.error(err?.message || 'Failed to upload logo');
          return;
        }
      }

      try {
        const refreshed = await projectService.getDesignSystem(projectId);
        const item = Array.isArray(refreshed) && refreshed.length > 0 ? refreshed[0] : refreshed;
        onUpdated?.(item || null);
      } catch {
        // ignore refresh errors
      }

      setHasUpdatedDesignSystem(true);
      setIsDirty(false);
      toast.success('Design system has been updated successfully');
      onAfterSave?.();
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setSaving(true);
    try {
      setDesignSystem({ ...DEFAULT_DESIGN_SYSTEM });
      setLogo(null);
      setPreviewUrl(null);
      setLogoRemoved(true);
      if (fileInputRef.current) fileInputRef.current.value = '';

      if (!projectId) {
        setShowResetDialog(false);
        return;
      }

      await projectService.updateDesignSystem(projectId, { ...DEFAULT_DESIGN_SYSTEM });

      if (initialHasLogo) {
        try {
          await projectService.deleteDesignSystemLogo(projectId);
          setInitialHasLogo(false);
        } catch (err: any) {
          toast.error(err?.message || 'Failed to remove logo');
        }
      }

      try {
        const refreshed = await projectService.getDesignSystem(projectId);
        const item = Array.isArray(refreshed) && refreshed.length > 0 ? refreshed[0] : refreshed;
        onUpdated?.(item || null);
      } catch {}

      toast.success('Design system values reset successfully');
      setIsDirty(false);
      onAfterSave?.();
    } finally {
      setSaving(false);
      setShowResetDialog(false);
    }
  };

  // Render a spinner instead of the form while the initial GET is in flight —
  // showing the DEFAULT_DESIGN_SYSTEM colors and then swapping them for the
  // saved ones a beat later reads like the panel just overwrote the user's
  // work. Skips when `autoLoad === false` or there's no `projectId` — those
  // callers are wiring their own state and don't need us to spin.
  if (isLoadingDesignSystem || isExtractingFromOnboarding) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-500">
        <Loader2 className="w-6 h-6 animate-spin" />
        <span className="ml-3 text-sm">
          {isExtractingFromOnboarding
            ? 'Building your design system from your screenshots…'
            : 'Loading design system…'}
        </span>
      </div>
    );
  }

  const scrollToSection = (id: DesignSystemSectionId) => {
    const container = centerScrollRef.current;
    const section = sectionRefs[id].current;
    if (container && section) {
      const top =
        section.getBoundingClientRect().top -
        container.getBoundingClientRect().top +
        container.scrollTop;
      container.scrollTo({ top: Math.max(0, top - 16), behavior: 'smooth' });
      return;
    }
    section?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <>
      <Dialog
        open={showResetDialog}
        onOpenChange={(open) => {
          if (!saving) setShowResetDialog(open);
        }}
      >
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-gray-900">
              Reset design system?
            </DialogTitle>
          </DialogHeader>
          <DialogDescription className="text-sm text-gray-600 leading-relaxed">
            Your design system keeps your prototype aligned with your brand colors, typography,
            spacing, and components. If you reset it, Ideoz will generate a generic UI without brand
            identity.
          </DialogDescription>
          <DialogFooter className="gap-3 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => setShowResetDialog(false)}
              disabled={saving}
              className="border-gray-300 text-gray-700"
            >
              Keep design system
            </Button>
            <Button
              onClick={handleReset}
              variant="destructive"
              disabled={saving}
              className="gap-2 bg-red-600 hover:bg-red-700 text-white"
            >
              {saving ? (
                <>
                  <Loader2 className="size-4 animate-spin" />
                  Resetting…
                </>
              ) : (
                'Reset'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {layout === 'page' ? (
        <div className="flex h-full min-h-0 flex-col">
          <DesignSystemPageLayout
            designSystem={designSystem}
            setDesignSystem={updateDesignSystem}
            previewUrl={previewUrl}
            isDirty={isDirty}
            saving={saving}
            isExtracting={isExtracting}
            extractFiles={extractFiles}
            extractPreviews={extractPreviews}
            isDragOver={isDragOver}
            sectionRefs={sectionRefs}
            centerScrollRef={centerScrollRef}
            onSectionClick={scrollToSection}
            onExtractClick={() => scrollToSection('brand')}
            onChooseScreenshots={() => extractInputRef.current?.click()}
            onExtract={handleExtract}
            onRemoveExtractFile={removeExtractFile}
            onLogoClick={() => fileInputRef.current?.click()}
            onLogoRemove={handleRemove}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onSave={handleSave}
            onReset={() => setShowResetDialog(true)}
            hasUpdatedDesignSystem={hasUpdatedDesignSystem}
          />
          <input
            ref={extractInputRef}
            type="file"
            accept="image/png,image/jpeg,image/jpg,image/webp"
            multiple
            onChange={(e) => {
              addExtractFiles(e.target.files);
              if (extractInputRef.current) extractInputRef.current.value = '';
            }}
            className="hidden"
          />
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleHiddenInputChange}
            className="hidden"
          />
        </div>
      ) : (
      <div className="space-y-6">
        <div className="rounded-lg border border-[#E1E1E1] bg-gradient-to-br from-[#EEF2FF]/50 to-white p-4 dark:border-gray-700 dark:from-gray-900 dark:to-gray-800">
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="w-4 h-4 text-[#4B579F]" />
            <Label className="text-[#4B579F] text-sm font-semibold">
              Extract from screenshots
            </Label>
          </div>
          <p className="text-xs text-gray-500 mb-3">
            Upload 2–5 screenshots for best results — a list view, the add-item flow, and the
            main navigation help detect both the visual style and interaction patterns.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => extractInputRef.current?.click()}
              className="border-gray-300 text-gray-700 text-sm dark:border-gray-600 dark:text-gray-200"
              disabled={isExtracting}
            >
              {extractFiles.length === 0 ? 'Choose screenshots' : 'Add more'}
            </Button>
            <Button
              type="button"
              onClick={handleExtract}
              className="bg-[#4B579F] text-white text-sm"
              disabled={isExtracting || extractFiles.length === 0}
            >
              {isExtracting ? 'Extracting…' : 'Extract'}
            </Button>
            <span className="text-xs text-gray-500">
              {extractFiles.length > 0
                ? `${extractFiles.length}/6 selected`
                : 'PNG / JPEG / WEBP, max 4 MB each, up to 6.'}
            </span>
          </div>
          {extractPreviews.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {extractPreviews.map((url, idx) => (
                <div
                  key={url}
                  className="relative w-16 h-16 rounded border border-[#E1E1E1] overflow-hidden bg-white dark:border-gray-700 dark:bg-gray-800"
                >
                  <img
                    src={url}
                    alt={`Screenshot ${idx + 1}`}
                    className="w-full h-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => removeExtractFile(idx)}
                    aria-label={`Remove screenshot ${idx + 1}`}
                    disabled={isExtracting}
                    className="absolute top-0.5 right-0.5 bg-white/90 rounded-full p-0.5 shadow hover:bg-white disabled:opacity-50"
                  >
                    <X className="w-3 h-3 text-gray-700" />
                  </button>
                </div>
              ))}
            </div>
          )}
          <input
            ref={extractInputRef}
            type="file"
            accept="image/png,image/jpeg,image/jpg,image/webp"
            multiple
            onChange={(e) => {
              addExtractFiles(e.target.files);
              if (extractInputRef.current) extractInputRef.current.value = '';
            }}
            className="hidden"
          />
        </div>
        <div>
          <Label className="text-[#4B579F] text-sm font-semibold">Upload your logo</Label>
          <div
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`flex flex-col items-center justify-center mt-3 py-4 rounded-md border transition-colors ${
              isDragOver
                ? 'border-blue-400 bg-blue-50 dark:bg-blue-950/40'
                : 'border-dashed border-[#E1E1E1] bg-gray-50 dark:border-gray-700 dark:bg-gray-800'
            }`}
          >
            {previewUrl ? (
              <div className="relative w-full flex items-center justify-center p-4 cursor-pointer">
                <img src={previewUrl} alt="Logo preview" className="max-h-28 object-contain" />
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemove();
                  }}
                  aria-label="Remove logo"
                  disabled={removingLogo || saving}
                  className="absolute top-2 right-2 bg-white rounded-full p-1 shadow hover:bg-gray-100 disabled:opacity-50"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#111827"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center gap-2 cursor-pointer">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="32"
                  height="32"
                  viewBox="0 0 32 32"
                  fill="none"
                >
                  <path
                    d="M5.69547 27.6372L14.4909 18.8418C15.0189 18.3138 15.2829 18.0498 15.5873 17.9509C15.8551 17.8639 16.1436 17.8639 16.4114 17.9509C16.7158 18.0498 16.9798 18.3138 17.5078 18.8418L26.2446 27.5786M18.666 20L22.4909 16.1752C23.0189 15.6471 23.2829 15.3831 23.5873 15.2842C23.8551 15.1972 24.1436 15.1972 24.4114 15.2842C24.7158 15.3831 24.9798 15.6471 25.5078 16.1752L29.3327 20M13.3327 12C13.3327 13.4728 12.1388 14.6667 10.666 14.6667C9.19326 14.6667 7.99935 13.4728 7.99935 12C7.99935 10.5272 9.19326 9.33333 10.666 9.33333C12.1388 9.33333 13.3327 10.5272 13.3327 12ZM9.06602 28H22.9327C25.1729 28 26.293 28 27.1486 27.564C27.9013 27.1805 28.5132 26.5686 28.8967 25.816C29.3327 24.9603 29.3327 23.8402 29.3327 21.6V10.4C29.3327 8.15979 29.3327 7.03969 28.8967 6.18404C28.5132 5.43139 27.9013 4.81947 27.1486 4.43597C26.293 4 25.1729 4 22.9327 4H9.06602C6.82581 4 5.7057 4 4.85005 4.43597C4.09741 4.81947 3.48548 5.43139 3.10199 6.18404C2.66602 7.03969 2.66602 8.15979 2.66602 10.4V21.6C2.66602 23.8402 2.66602 24.9603 3.10199 25.816C3.48548 26.5686 4.09741 27.1805 4.85005 27.564C5.7057 28 6.82581 28 9.06602 28Z"
                    stroke="#252B37"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <div className="flex flex-col items-center">
                  {logo?.fileName ? (
                    <span className="flex text-[12px] text-[#333A63] dark:text-gray-200">
                      Selected: {logo.fileName}
                    </span>
                  ) : (
                    <span className="flex text-[12px] text-[#333A63] dark:text-gray-200">
                      Drag & drop or click to add your logo file
                    </span>
                  )}
                  <span className="flex text-[10px] text-gray-500 dark:text-gray-400">JPG, PNG, SVG file</span>
                  <span className="flex text-[10px] text-gray-700 dark:text-gray-300">Max size: 4 MB per file</span>
                </div>
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleHiddenInputChange}
              className="hidden"
            />
          </div>
        </div>
        <h4 className="text-[#4B579F] text-sm font-semibold">Select your brand color system</h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-secondry-700 text-base text-medium">Primary</Label>
            <ColorPicker
              label="Primary"
              value={designSystem.primaryColor || '#3B82F6'}
              onChange={(v) => setDesignSystem((s) => ({ ...s, primaryColor: v }))}
            />
          </div>
          <div>
            <Label className="text-secondry-700 text-base text-medium">Primary foreground</Label>
            <ColorPicker
              label="Primary/Foreground"
              value={designSystem.primaryForeground || '#FFFFFF'}
              onChange={(v) => setDesignSystem((s) => ({ ...s, primaryForeground: v }))}
            />
          </div>
          <div>
            <Label className="text-secondry-700 text-base text-medium">Secondary</Label>
            <ColorPicker
              label="Secondary"
              value={designSystem.secondaryColor || '#8B5CF6'}
              onChange={(v) => setDesignSystem((s) => ({ ...s, secondaryColor: v }))}
            />
          </div>
          <div>
            <Label className="text-secondry-700 text-base text-medium">Secondary foreground</Label>
            <ColorPicker
              label="Secondary/Foreground"
              value={designSystem.secondaryForeground || '#FFFFFF'}
              onChange={(v) => setDesignSystem((s) => ({ ...s, secondaryForeground: v }))}
            />
          </div>
        </div>
        <div className="mt-6">
          <h4 className="text-[#4B579F] text-sm font-semibold mb-2">Semantic colors</h4>
          <p className="text-xs text-gray-500 mb-3">
            Used for status pills, alerts, and form validation.
          </p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-secondry-700 text-base text-medium">Success</Label>
              <ColorPicker
                label="Success"
                value={designSystem.successColor || '#22C55E'}
                onChange={(v) => setDesignSystem((s) => ({ ...s, successColor: v }))}
              />
            </div>
            <div>
              <Label className="text-secondry-700 text-base text-medium">Warning</Label>
              <ColorPicker
                label="Warning"
                value={designSystem.warningColor || '#EAB308'}
                onChange={(v) => setDesignSystem((s) => ({ ...s, warningColor: v }))}
              />
            </div>
            <div>
              <Label className="text-secondry-700 text-base text-medium">Error</Label>
              <ColorPicker
                label="Error"
                value={designSystem.errorColor || '#EF4444'}
                onChange={(v) => setDesignSystem((s) => ({ ...s, errorColor: v }))}
              />
            </div>
            <div>
              <Label className="text-secondry-700 text-base text-medium">Info</Label>
              <ColorPicker
                label="Info"
                value={designSystem.infoColor || '#3B82F6'}
                onChange={(v) => setDesignSystem((s) => ({ ...s, infoColor: v }))}
              />
            </div>
          </div>
        </div>
        <div className="mt-6">
          <h4 className="text-[#4B579F] text-sm font-semibold mb-2">Neutral palette</h4>
          <p className="text-xs text-gray-500 mb-3">
            Card surfaces, dividers, and muted text — what gives the app its layered feel.
          </p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-secondry-700 text-base text-medium">Surface</Label>
              <ColorPicker
                label="Surface"
                value={designSystem.surfaceColor || '#F8FAFC'}
                onChange={(v) => setDesignSystem((s) => ({ ...s, surfaceColor: v }))}
              />
            </div>
            <div>
              <Label className="text-secondry-700 text-base text-medium">Border</Label>
              <ColorPicker
                label="Border"
                value={designSystem.borderColor || '#E2E8F0'}
                onChange={(v) => setDesignSystem((s) => ({ ...s, borderColor: v }))}
              />
            </div>
            <div>
              <Label className="text-secondry-700 text-base text-medium">Muted text</Label>
              <ColorPicker
                label="Muted text"
                value={designSystem.mutedTextColor || '#64748B'}
                onChange={(v) => setDesignSystem((s) => ({ ...s, mutedTextColor: v }))}
              />
            </div>
          </div>
        </div>
        <div>
          <h4 className="text-[#4B579F] text-sm font-semibold">Define your typography</h4>
          <div className="mt-2 grid grid-cols-2 gap-4">
            <div>
              <Label className="text-secondry-700 text-base text-medium">Body font</Label>
              <Select
                value={designSystem.fontFamily}
                onValueChange={(v) => setDesignSystem((s) => ({ ...s, fontFamily: v }))}
              >
                <SelectTrigger className="w-full justify-between mt-2">
                  <SelectValue placeholder={designSystem.fontFamily} />
                </SelectTrigger>
                <SelectContent>
                  {DEFAULT_FONTS.map((f) => (
                    <SelectItem key={f} value={f}>
                      {f}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-secondry-700 text-base text-medium">
                Heading font
                <span className="ml-1 text-xs text-gray-400 font-normal">
                  (optional; falls back to body)
                </span>
              </Label>
              <Select
                value={designSystem.headingFontFamily ?? ''}
                onValueChange={(v) =>
                  setDesignSystem((s) => ({
                    ...s,
                    headingFontFamily: v === '__none__' ? undefined : v,
                  }))
                }
              >
                <SelectTrigger className="w-full justify-between mt-2">
                  <SelectValue placeholder="Same as body" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Same as body</SelectItem>
                  {DEFAULT_FONTS.map((f) => (
                    <SelectItem key={f} value={f}>
                      {f}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <div className="mt-6">
          <h4 className="text-[#4B579F] text-sm font-semibold mb-2">
            What is your Prototype Theme?
          </h4>
          <div className="flex gap-3">
            <div
              className={`px-4 py-2 rounded-full border cursor-pointer text-sm font-medium transition-colors flex items-center gap-2
            ${designSystem.theme === 'light' || !designSystem.theme ? 'bg-[#ecfdf3] border-[#2af37c] text-[#347c5c]' : 'bg-white border-[#E1E1E1] text-gray-600 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300'}`}
              onClick={() => setDesignSystem((s) => ({ ...s, theme: 'light' }))}
              aria-pressed={designSystem.theme === 'light' || !designSystem.theme}
              tabIndex={0}
            >
              {(designSystem.theme === 'light' || !designSystem.theme) && (
                <Icon icon={Check} size={18} strokeWidth={2} color="#2af37c" />
              )}
              Light theme
            </div>
            <div
              className={`px-4 py-2 rounded-full border cursor-pointer text-sm font-medium transition-colors flex items-center gap-2
            ${designSystem.theme === 'dark' ? 'bg-[#ecfdf3] border-[#2af37c] text-[#347c5c]' : 'bg-white border-[#E1E1E1] text-gray-600 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300'}`}
              onClick={() => setDesignSystem((s) => ({ ...s, theme: 'dark' }))}
              aria-pressed={designSystem.theme === 'dark'}
              tabIndex={0}
            >
              {designSystem.theme === 'dark' && (
                <Icon icon={Check} size={18} strokeWidth={2} color="#2af37c" />
              )}
              Dark theme
            </div>
          </div>
        </div>
        <div className="mt-6">
          <h4 className="text-[#4B579F] text-sm font-semibold mb-2">Visual style</h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-secondry-700 text-base text-medium">App background</Label>
              <ColorPicker
                label="App background"
                value={designSystem.appBg || '#FFFFFF'}
                onChange={(v) => setDesignSystem((s) => ({ ...s, appBg: v }))}
              />
            </div>
            <div>
              <Label className="text-secondry-700 text-base text-medium">Border radius</Label>
              <Select
                value={designSystem.borderRadius}
                onValueChange={(v) =>
                  setDesignSystem((s) => ({ ...s, borderRadius: v as DesignSystem['borderRadius'] }))
                }
              >
                <SelectTrigger className="w-full justify-between mt-2">
                  <SelectValue placeholder="Border radius" />
                </SelectTrigger>
                <SelectContent>
                  {BORDER_RADIUS_OPTIONS.map((opt) => (
                    <SelectItem key={opt} value={opt as string}>
                      {opt}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-secondry-700 text-base text-medium">Shadows</Label>
              <Select
                value={designSystem.shadows}
                onValueChange={(v) =>
                  setDesignSystem((s) => ({ ...s, shadows: v as DesignSystem['shadows'] }))
                }
              >
                <SelectTrigger className="w-full justify-between mt-2">
                  <SelectValue placeholder="Shadows" />
                </SelectTrigger>
                <SelectContent>
                  {SHADOWS_OPTIONS.map((opt) => (
                    <SelectItem key={opt} value={opt as string}>
                      {opt}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-secondry-700 text-base text-medium">Density</Label>
              <Select
                value={designSystem.density}
                onValueChange={(v) =>
                  setDesignSystem((s) => ({ ...s, density: v as DesignSystem['density'] }))
                }
              >
                <SelectTrigger className="w-full justify-between mt-2">
                  <SelectValue placeholder="Density" />
                </SelectTrigger>
                <SelectContent>
                  {DENSITY_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-secondry-700 text-base text-medium">Elevation style</Label>
              <Select
                value={designSystem.elevationStyle}
                onValueChange={(v) =>
                  setDesignSystem((s) => ({
                    ...s,
                    elevationStyle: v as DesignSystem['elevationStyle'],
                  }))
                }
              >
                <SelectTrigger className="w-full justify-between mt-2">
                  <SelectValue placeholder="Elevation style" />
                </SelectTrigger>
                <SelectContent>
                  {ELEVATION_STYLE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-secondry-700 text-base text-medium">Button style</Label>
              <Select
                value={designSystem.buttonStyle}
                onValueChange={(v) =>
                  setDesignSystem((s) => ({
                    ...s,
                    buttonStyle: v as DesignSystem['buttonStyle'],
                  }))
                }
              >
                <SelectTrigger className="w-full justify-between mt-2">
                  <SelectValue placeholder="Button style" />
                </SelectTrigger>
                <SelectContent>
                  {BUTTON_STYLE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-secondry-700 text-base text-medium">Icon style</Label>
              <Select
                value={designSystem.iconStyle}
                onValueChange={(v) =>
                  setDesignSystem((s) => ({ ...s, iconStyle: v as DesignSystem['iconStyle'] }))
                }
              >
                <SelectTrigger className="w-full justify-between mt-2">
                  <SelectValue placeholder="Icon style" />
                </SelectTrigger>
                <SelectContent>
                  {ICON_STYLE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-secondry-700 text-base text-medium">Input style</Label>
              <Select
                value={designSystem.inputStyle}
                onValueChange={(v) =>
                  setDesignSystem((s) => ({ ...s, inputStyle: v as DesignSystem['inputStyle'] }))
                }
              >
                <SelectTrigger className="w-full justify-between mt-2">
                  <SelectValue placeholder="Input style" />
                </SelectTrigger>
                <SelectContent>
                  {INPUT_STYLE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <div className="mt-6">
          <h4 className="text-[#4B579F] text-sm font-semibold mb-2">Interaction patterns</h4>
          <p className="text-xs text-gray-500 mb-3">
            How your app behaves — used so the generated prototype matches your app's UX.
          </p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-secondry-700 text-base text-medium">Add new item</Label>
              <Select
                value={designSystem.uxPatterns?.addPattern}
                onValueChange={(v) =>
                  setDesignSystem((s) => ({
                    ...s,
                    uxPatterns: {
                      ...(s.uxPatterns ?? {}),
                      addPattern: v as UxPatterns['addPattern'],
                    },
                  }))
                }
              >
                <SelectTrigger className="w-full justify-between mt-2">
                  <SelectValue placeholder="Add pattern" />
                </SelectTrigger>
                <SelectContent>
                  {FORM_PATTERN_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-secondry-700 text-base text-medium">Edit item</Label>
              <Select
                value={designSystem.uxPatterns?.editPattern}
                onValueChange={(v) =>
                  setDesignSystem((s) => ({
                    ...s,
                    uxPatterns: {
                      ...(s.uxPatterns ?? {}),
                      editPattern: v as UxPatterns['editPattern'],
                    },
                  }))
                }
              >
                <SelectTrigger className="w-full justify-between mt-2">
                  <SelectValue placeholder="Edit pattern" />
                </SelectTrigger>
                <SelectContent>
                  {FORM_PATTERN_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-secondry-700 text-base text-medium">Detail view</Label>
              <Select
                value={designSystem.uxPatterns?.detailPattern}
                onValueChange={(v) =>
                  setDesignSystem((s) => ({
                    ...s,
                    uxPatterns: {
                      ...(s.uxPatterns ?? {}),
                      detailPattern: v as UxPatterns['detailPattern'],
                    },
                  }))
                }
              >
                <SelectTrigger className="w-full justify-between mt-2">
                  <SelectValue placeholder="Detail pattern" />
                </SelectTrigger>
                <SelectContent>
                  {DETAIL_PATTERN_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-secondry-700 text-base text-medium">Delete confirmation</Label>
              <Select
                value={designSystem.uxPatterns?.deletePattern}
                onValueChange={(v) =>
                  setDesignSystem((s) => ({
                    ...s,
                    uxPatterns: {
                      ...(s.uxPatterns ?? {}),
                      deletePattern: v as UxPatterns['deletePattern'],
                    },
                  }))
                }
              >
                <SelectTrigger className="w-full justify-between mt-2">
                  <SelectValue placeholder="Delete pattern" />
                </SelectTrigger>
                <SelectContent>
                  {DELETE_PATTERN_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-secondry-700 text-base text-medium">Navigation</Label>
              <Select
                value={designSystem.uxPatterns?.navPattern}
                onValueChange={(v) =>
                  setDesignSystem((s) => ({
                    ...s,
                    uxPatterns: {
                      ...(s.uxPatterns ?? {}),
                      navPattern: v as UxPatterns['navPattern'],
                    },
                  }))
                }
              >
                <SelectTrigger className="w-full justify-between mt-2">
                  <SelectValue placeholder="Nav pattern" />
                </SelectTrigger>
                <SelectContent>
                  {NAV_PATTERN_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-secondry-700 text-base text-medium">List display</Label>
              <Select
                value={designSystem.uxPatterns?.listPattern}
                onValueChange={(v) =>
                  setDesignSystem((s) => ({
                    ...s,
                    uxPatterns: {
                      ...(s.uxPatterns ?? {}),
                      listPattern: v as UxPatterns['listPattern'],
                    },
                  }))
                }
              >
                <SelectTrigger className="w-full justify-between mt-2">
                  <SelectValue placeholder="List pattern" />
                </SelectTrigger>
                <SelectContent>
                  {LIST_PATTERN_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-secondry-700 text-base text-medium">Container width</Label>
              <Select
                value={designSystem.uxPatterns?.containerWidth}
                onValueChange={(v) =>
                  setDesignSystem((s) => ({
                    ...s,
                    uxPatterns: {
                      ...(s.uxPatterns ?? {}),
                      containerWidth: v as UxPatterns['containerWidth'],
                    },
                  }))
                }
              >
                <SelectTrigger className="w-full justify-between mt-2">
                  <SelectValue placeholder="Container width" />
                </SelectTrigger>
                <SelectContent>
                  {CONTAINER_WIDTH_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-secondry-700 text-base text-medium">Sidebar layout</Label>
              <Select
                value={designSystem.uxPatterns?.sidebarLayout}
                onValueChange={(v) =>
                  setDesignSystem((s) => ({
                    ...s,
                    uxPatterns: {
                      ...(s.uxPatterns ?? {}),
                      sidebarLayout: v as UxPatterns['sidebarLayout'],
                    },
                  }))
                }
              >
                <SelectTrigger className="w-full justify-between mt-2">
                  <SelectValue placeholder="Sidebar layout" />
                </SelectTrigger>
                <SelectContent>
                  {SIDEBAR_LAYOUT_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-secondry-700 text-base text-medium">Search</Label>
              <Select
                value={designSystem.uxPatterns?.searchPattern}
                onValueChange={(v) =>
                  setDesignSystem((s) => ({
                    ...s,
                    uxPatterns: {
                      ...(s.uxPatterns ?? {}),
                      searchPattern: v as UxPatterns['searchPattern'],
                    },
                  }))
                }
              >
                <SelectTrigger className="w-full justify-between mt-2">
                  <SelectValue placeholder="Search pattern" />
                </SelectTrigger>
                <SelectContent>
                  {SEARCH_PATTERN_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-secondry-700 text-base text-medium">Filters</Label>
              <Select
                value={designSystem.uxPatterns?.filterPattern}
                onValueChange={(v) =>
                  setDesignSystem((s) => ({
                    ...s,
                    uxPatterns: {
                      ...(s.uxPatterns ?? {}),
                      filterPattern: v as UxPatterns['filterPattern'],
                    },
                  }))
                }
              >
                <SelectTrigger className="w-full justify-between mt-2">
                  <SelectValue placeholder="Filter pattern" />
                </SelectTrigger>
                <SelectContent>
                  {FILTER_PATTERN_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-secondry-700 text-base text-medium">Loading state</Label>
              <Select
                value={designSystem.uxPatterns?.loadingPattern}
                onValueChange={(v) =>
                  setDesignSystem((s) => ({
                    ...s,
                    uxPatterns: {
                      ...(s.uxPatterns ?? {}),
                      loadingPattern: v as UxPatterns['loadingPattern'],
                    },
                  }))
                }
              >
                <SelectTrigger className="w-full justify-between mt-2">
                  <SelectValue placeholder="Loading pattern" />
                </SelectTrigger>
                <SelectContent>
                  {LOADING_PATTERN_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-secondry-700 text-base text-medium">Empty state</Label>
              <Select
                value={designSystem.uxPatterns?.emptyStatePattern}
                onValueChange={(v) =>
                  setDesignSystem((s) => ({
                    ...s,
                    uxPatterns: {
                      ...(s.uxPatterns ?? {}),
                      emptyStatePattern: v as UxPatterns['emptyStatePattern'],
                    },
                  }))
                }
              >
                <SelectTrigger className="w-full justify-between mt-2">
                  <SelectValue placeholder="Empty state pattern" />
                </SelectTrigger>
                <SelectContent>
                  {EMPTY_STATE_PATTERN_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 pt-4">
          {hasUpdatedDesignSystem ? (
            <>
              <Button
                variant="outline"
                onClick={() => setShowResetDialog(true)}
                className="border-gray-300 text-gray-700 text-sm dark:border-gray-600 dark:text-gray-200"
                disabled={saving}
              >
                Reset
              </Button>
              <Button
                onClick={handleSave}
                className="bg-[#1E2340] text-white text-sm"
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Update'}
              </Button>
            </>
          ) : (
            <>
              {onCancel && (
                <Button
                  variant="outline"
                  onClick={onCancel}
                  className="border-0 text-sm"
                  disabled={saving}
                >
                  Cancel
                </Button>
              )}
              <Button
                onClick={handleSave}
                className="bg-[#1E2340] text-white text-sm"
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Update'}
              </Button>
            </>
          )}
        </div>
      </div>
      )}
    </>
  );
};

export default DesignSystemPanel;
